import { Copy, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import {
  buildStoreUrl,
  useTranslation,
  type OfferCampaignStatus,
  type OfferCampaignWithItems,
  type TranslationKey,
} from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

export interface OfferCampaignsListProps {
  readonly campaigns: readonly OfferCampaignWithItems[];
  readonly storeSlug: string;
  readonly onEdit: (campaign: OfferCampaignWithItems) => void;
  readonly onDelete: (campaign: OfferCampaignWithItems) => void;
  readonly onLinkCopied?: () => void;
}

function statusVariant(status: OfferCampaignStatus): 'success' | 'warning' | 'neutral' | 'info' {
  if (status === 'live') return 'success';
  if (status === 'scheduled') return 'info';
  if (status === 'ended') return 'neutral';
  return 'warning';
}

function campaignUrl(storeSlug: string, publicSlug: string): string {
  const url = new URL(buildStoreUrl(storeSlug));
  url.pathname = `/offers/${encodeURIComponent(publicSlug)}`;
  return url.toString();
}

function formatDate(value: string, locale: string): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return formatter.format(new Date(value));
}

export function OfferCampaignsList({
  campaigns,
  storeSlug,
  onEdit,
  onDelete,
  onLinkCopied,
}: OfferCampaignsListProps) {
  const { t, dir, lang } = useTranslation();
  const clipboard = useCopyToClipboard();

  async function copyCampaignLink(campaign: OfferCampaignWithItems): Promise<void> {
    const didCopy = await clipboard.copy(campaignUrl(storeSlug, campaign.public_slug));
    if (didCopy) onLinkCopied?.();
  }

  return (
    <div
      dir={dir}
      data-testid="offer-campaigns-list"
      className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
    >
      <ul className="divide-border divide-y">
        {campaigns.map((campaign) => {
          const url = campaignUrl(storeSlug, campaign.public_slug);
          return (
            <li
              key={campaign.id}
              data-testid={`offer-campaign-row-${campaign.id}`}
              className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p
                    className="text-foreground min-w-0 truncate text-start font-medium"
                    data-testid={`offer-campaign-row-${campaign.id}-title`}
                  >
                    {campaign.title}
                  </p>
                  <Badge variant={statusVariant(campaign.status)}>
                    {t(`admin.offerCampaigns.status.${campaign.status}` as TranslationKey)}
                  </Badge>
                  {!campaign.is_active ? (
                    <Badge variant="warning">
                      {t('admin.offerCampaigns.status.inactive' as TranslationKey)}
                    </Badge>
                  ) : null}
                </div>
                {campaign.short_description ? (
                  <p className="text-muted-foreground mt-1 line-clamp-2 text-start text-sm">
                    {campaign.short_description}
                  </p>
                ) : null}
                <p
                  className="text-muted-foreground mt-2 text-start text-xs"
                  data-testid={`offer-campaign-row-${campaign.id}-date-range`}
                >
                  {t('admin.offerCampaigns.dateRange' as TranslationKey, {
                    start: formatDate(campaign.starts_at, lang),
                    end: formatDate(campaign.ends_at, lang),
                    interpolation: { escapeValue: false },
                  })}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="neutral">
                    {t('admin.offerCampaigns.picker.selected' as TranslationKey, {
                      count: campaign.items.length,
                    })}
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('admin.offerCampaigns.copyLink' as TranslationKey)}
                  disabled={clipboard.isCopying}
                  onClick={() => {
                    void copyCampaignLink(campaign);
                  }}
                >
                  <Copy className="size-4" aria-hidden />
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  aria-label={t('admin.offerCampaigns.publicLink' as TranslationKey)}
                >
                  <a href={url} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-4" aria-hidden />
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('admin.offerCampaigns.editCampaign' as TranslationKey, {
                    title: campaign.title,
                  })}
                  onClick={() => onEdit(campaign)}
                >
                  <Pencil className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-sand-600 hover:text-terracotta dark:text-sand-300"
                  aria-label={t('common.delete')}
                  onClick={() => onDelete(campaign)}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
